import { createContext, useState, type ReactNode, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { UserLoader } from '@repo/ui/UserLoader';

import { useUser } from '@repo/hooks/useUser';
import GET_ACCOUNT from '@/gql/getAccount.gql';
import type { Mercury } from '@repo/types/Models';
import { useParams } from 'react-router';
import { DeactivatedAccount } from '@/components/DeactivatedAccount';

export interface AccountProps {
  children?: ReactNode;
}

const context = createContext<Mercury.Models.Account | null>(null);
const { Provider } = context;
export const AccountContextContext = context;

const ServerUserContext = () => {
  // Maybe it would be good to show something different on server, but I am uncertain
  // How to implement that best.
  return <UserLoader />;
};

export const AccountContext = ({ children }: AccountProps) => {
  const { accountId } = useParams();
  const requestedId = Array.isArray(accountId) ? accountId[0] : accountId;

  const { data, error, loading } = useQuery(GET_ACCOUNT, {
    variables: { id: requestedId },
    // `id` is a non-nullable `ID!`. On the `/login` -> `/` landing there is no
    // `accountId` route param yet, so firing the query would send `id: null` and
    // the server rejects it (SWIFT-CLIENT-DASHBOARD-6). Skip until we have an id;
    // the render below falls back to the user's primary account in that window.
    skip: !requestedId,
    notifyOnNetworkStatusChange: true,
  });
  if (error) {
    console.error(error);
  }

  const currentAccount = data?.account;

  const user = useUser();
  const userPrimaryAccount = user.account;

  const [isClient, setIsClient] = useState<boolean>(false);
  useEffect(() => setIsClient(true), []);

  // Show loading if:
  // 1. Apollo is loading
  // 2. OR the fetched account ID doesn't match the requested ID (stale data)
  const isStaleData = currentAccount && currentAccount.id !== requestedId;
  if (loading || isStaleData) {
    return <ServerUserContext />;
  }

  const account = currentAccount || userPrimaryAccount;

  if (account?.deactivatedAt) {
    return <DeactivatedAccount />;
  }

  return isClient ? <Provider value={account}>{children}</Provider> : <ServerUserContext />;
};
